// Drei-Wege-Merge fuer Bilder-Arrays, gebraucht wenn ein Upload wegen eines Konflikts abgelehnt
// wurde (siehe sql.php baseId-Check). "base" ist der Stand, von dem die lokalen Aenderungen
// ausgehen, "local" der eigene bearbeitete Stand, "remote" der inzwischen von jemand anderem
// gespeicherte Stand. Bilder werden ueber ihre "id" einander zugeordnet.
//
// Aenderungen, die nur auf einer Seite passiert sind (nur lokal ODER nur remote veraendert/
// geloescht/neu), werden automatisch uebernommen. Wurde dasselbe Bild von beiden Seiten
// unterschiedlich veraendert bzw. eine Seite hat es geloescht, waehrend die andere es bearbeitet
// hat, gilt das als Konflikt: es wird die Version behalten, die aktiv bearbeitet wurde (also nie
// stillschweigend geloescht), aber als konfliktbehaftet gemeldet, damit der Nutzer es pruefen kann

const CONTENT_FIELDS = ['title', 'point', 'comment', 'leaders', 'followers']

const hasId = (bild) => bild.id !== undefined && bild.id !== null

const bildContentEquals = (a, b) => CONTENT_FIELDS.every(f => JSON.stringify(a?.[f]) === JSON.stringify(b?.[f]))

const idMapOf = (bilder) => {
    const map = new Map()
    bilder.forEach(b => {
        if (hasId(b)) map.set(b.id, b)
    })
    return map
}

const describeBild = (bild) => [bild.title, bild.point].filter(Boolean).join(' - ') || '(ohne Titel)'

// Setzt die id-losen "newIds" (in ihrer Reihenfolge aus referenceOrder) so in outputIds ein, dass
// sie moeglichst nah an ihrer Position im Referenz-Array bleiben - gesucht wird dafuer je Element
// rueckwaerts (dann vorwaerts) nach dem naechsten bereits platzierten Nachbarn
function insertByReferenceOrder(outputIds, newIds, referenceOrder) {
    for (const id of newIds) {
        if (outputIds.includes(id)) continue
        const refIdx = referenceOrder.indexOf(id)
        let insertAt = outputIds.length
        for (let i = refIdx - 1; i >= 0; i--) {
            const idx = outputIds.indexOf(referenceOrder[i])
            if (idx !== -1) {
                insertAt = idx + 1
                break
            }
        }
        if (insertAt === outputIds.length) {
            for (let i = refIdx + 1; i < referenceOrder.length; i++) {
                const idx = outputIds.indexOf(referenceOrder[i])
                if (idx !== -1) {
                    insertAt = idx
                    break
                }
            }
        }
        outputIds.splice(insertAt, 0, id)
    }
}

export function mergeBilder(base, local, remote) {
    const baseMap = idMapOf(base)
    const localMap = idMapOf(local)
    const remoteMap = idMapOf(remote)
    const allIds = new Set([...baseMap.keys(), ...localMap.keys(), ...remoteMap.keys()])

    const keep = new Map()
    const duplicates = [] // Id-Kollisionen bei unabhaengig von beiden Seiten neu angelegten Bildern

    // Meldungen werden erst als {bilder, render}-Paar gesammelt und erst ganz am Ende (wenn die
    // endgueltige Reihenfolge feststeht) zu Text gerendert - so kann jede Meldung die Bildnummer
    // (Position im fertigen Ergebnis) nennen. "bilder" sind dabei Objekt-Referenzen, ueber die die
    // Position nachtraeglich per indexOf im fertigen Array gefunden wird
    const autoNoteBuilders = []
    const conflictNoteBuilders = []
    const pushAuto = (bilder, render) => autoNoteBuilders.push({bilder, render})
    const pushConflict = (bilder, render) => conflictNoteBuilders.push({bilder, render})

    for (const id of allIds) {
        const inBase = baseMap.has(id), inLocal = localMap.has(id), inRemote = remoteMap.has(id)

        if (!inBase) {
            if (inLocal && inRemote) {
                if (bildContentEquals(localMap.get(id), remoteMap.get(id))) {
                    keep.set(id, remoteMap.get(id))
                } else {
                    const keptBild = remoteMap.get(id)
                    const dupBild = {...localMap.get(id), id: null}
                    keep.set(id, keptBild)
                    duplicates.push({afterId: id, bild: dupBild})
                    pushConflict([keptBild, dupBild], pos => `Neues Bild "${describeBild(localMap.get(id))}" an Position ${pos[0]}/${pos[1]}: du und jemand anderes haben unabhängig voneinander ein neues Bild angelegt - bitte Reihenfolge/Nummer prüfen`)
                }
            } else if (inLocal) {
                const bild = localMap.get(id)
                keep.set(id, bild)
                pushAuto([bild], pos => `Bild ${pos[0]} "${describeBild(bild)}": neu von dir hinzugefügt`)
            } else {
                const bild = remoteMap.get(id)
                keep.set(id, bild)
                pushAuto([bild], pos => `Bild ${pos[0]} "${describeBild(bild)}": neu von jemand anderem hinzugefügt`)
            }
            continue
        }

        const label = describeBild(baseMap.get(id))
        const localChanged = inLocal && !bildContentEquals(baseMap.get(id), localMap.get(id))
        const remoteChanged = inRemote && !bildContentEquals(baseMap.get(id), remoteMap.get(id))

        if (!inLocal && !inRemote) continue // von beiden geloescht

        if (!inLocal) {
            if (remoteChanged) {
                const bild = remoteMap.get(id)
                keep.set(id, bild)
                pushConflict([bild], pos => `Bild ${pos[0]} "${label}" wurde von dir gelöscht, aber von jemand anderem bearbeitet - die Bearbeitung wurde beibehalten, bitte prüfen`)
            } // sonst: nur lokal geloescht, remote unveraendert -> Loeschung uebernehmen
            continue
        }
        if (!inRemote) {
            if (localChanged) {
                const bild = localMap.get(id)
                keep.set(id, bild)
                pushConflict([bild], pos => `Bild ${pos[0]} "${label}" wurde von jemand anderem gelöscht, deine Bearbeitung wurde aber beibehalten - bitte prüfen`)
            } else {
                autoNoteBuilders.push({bilder: [], render: () => `Bild "${label}" (von jemand anderem gelöscht) übernommen`})
            }
            continue
        }

        if (!localChanged && !remoteChanged) {
            keep.set(id, localMap.get(id))
        } else if (localChanged && !remoteChanged) {
            const bild = localMap.get(id)
            keep.set(id, bild)
            pushAuto([bild], pos => `Bild ${pos[0]} "${label}": deine Änderung übernommen`)
        } else if (!localChanged && remoteChanged) {
            const bild = remoteMap.get(id)
            keep.set(id, bild)
            pushAuto([bild], pos => `Bild ${pos[0]} "${label}": Änderung von jemand anderem übernommen`)
        } else if (bildContentEquals(localMap.get(id), remoteMap.get(id))) {
            keep.set(id, localMap.get(id))
        } else {
            const bild = localMap.get(id)
            keep.set(id, bild)
            pushConflict([bild], pos => `Bild ${pos[0]} "${label}" wurde von dir UND von jemand anderem unterschiedlich verändert - deine Version wurde übernommen, bitte mit dem aktuellen Stand abgleichen`)
        }
    }

    // Reihenfolge: Skelett aus der Reihenfolge von "remote" (das ist bereits der geteilte Stand).
    // Alles, was zu behalten ist, aber in remote (noch) nicht vorkommt - neu hinzugefuegte Bilder
    // ebenso wie lokal bearbeitete Bilder, die remote geloescht hatte (Konfliktfall) - wird anhand
    // seiner Position im lokalen Array eingefuegt
    const outputIds = remote.filter(b => hasId(b) && keep.has(b.id)).map(b => b.id)
    const localOrder = local.filter(hasId).map(b => b.id)
    const missingIds = localOrder.filter(id => keep.has(id) && !outputIds.includes(id))
    insertByReferenceOrder(outputIds, missingIds, localOrder)

    let merged = outputIds.map(id => keep.get(id))

    for (const dup of duplicates) {
        const idx = merged.findIndex(b => b.id === dup.afterId)
        merged.splice(idx === -1 ? merged.length : idx + 1, 0, dup.bild)
    }

    // Jetzt steht die endgueltige Reihenfolge fest - Meldungen mit der tatsaechlichen Bildnummer
    // (1-basiert, wie auch sonst in der App als "Bild N" angezeigt) rendern
    const positionsOf = (bilder) => bilder.map(b => merged.indexOf(b) + 1)
    const autoNotes = autoNoteBuilders.map(({bilder, render}) => render(positionsOf(bilder)))
    const conflictNotes = conflictNoteBuilders.map(({bilder, render}) => render(positionsOf(bilder)))

    // Bilder ganz ohne id (kaputte Altdaten) koennen ueber die id nicht zugeordnet werden - werden
    // sicherheitshalber unveraendert angehaengt statt sie zu verwerfen, und gemeldet
    const idless = [...local.filter(b => !hasId(b)), ...remote.filter(b => !hasId(b))]
    if (idless.length > 0) {
        const startPos = merged.length + 1
        merged = [...merged, ...idless]
        conflictNotes.push(`${idless.length} Bild(er) ohne eindeutige Nummer konnten nicht automatisch zugeordnet werden und wurden ab Position ${startPos} angehängt - bitte Reihenfolge prüfen`)
    }

    return {merged, autoNotes, conflictNotes}
}
